import React, { useState, useEffect, Fragment } from 'react';
import { Dialog, Transition, Combobox } from '@headlessui/react';
import { XMarkIcon, ExclamationTriangleIcon, CheckIcon, ChevronUpDownIcon } from '@heroicons/react/24/outline';
import { useQuery } from '@tanstack/react-query';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useIsServiceDeskUser } from '../hooks/useIsServiceDeskUser';
import { fetchAssignmentGroupMembers } from '../utils/groupMembers';

interface MoveToAssessModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (updatedProblem: any) => void;
  problemId: string;
  currentState: string;
  currentProblem?: any;
  onShowGroupChangeConfirmation?: (data: {
    newGroupId: string;
    newGroupName: string;
    pendingPayload: any;
  }) => void;
}

const MoveToAssessModal: React.FC<MoveToAssessModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  problemId,
  currentState: _currentState,
  currentProblem,
  onShowGroupChangeConfirmation
}) => {
  const [assignmentGroupId, setAssignmentGroupId] = useState<string>('');
  const [assignedToId, setAssignedToId] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [groupQuery, setGroupQuery] = useState('');
  const [userQuery, setUserQuery] = useState('');

  const { user } = useAuth();
  const isServiceDeskUser = useIsServiceDeskUser();

  // Fetch assessment groups (only type: assessment)
  const { data: groupsData, isLoading: loadingGroups } = useQuery({
    queryKey: ['assessment-groups'],
    queryFn: async () => {
      const response = await api.get('/groups/assessment-groups');
      return response.data;
    },
    enabled: isOpen
  });

  // Fetch users based on assignment group
  const { data: usersData, isLoading: loadingUsers } = useQuery({
    queryKey: ['group-users', assignmentGroupId, currentProblem?.customerId || currentProblem?.Customer?.id],
    queryFn: async () => {
      return fetchAssignmentGroupMembers(
        assignmentGroupId,
        currentProblem?.customerId || currentProblem?.Customer?.id || null
      );
    },
    enabled: !!assignmentGroupId && isOpen
  });

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setAssignmentGroupId('');
      setAssignedToId('');
      setError(null);
      setGroupQuery('');
      setUserQuery('');
    }
  }, [isOpen]);

  // Reset assigned to when assignment group changes
  useEffect(() => {
    setAssignedToId('');
    setUserQuery('');
  }, [assignmentGroupId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!assignmentGroupId) {
      setError('Assignment Group is required');
      return;
    }
    if (!assignedToId) {
      setError('Assigned To is required');
      return;
    }

    // Check if user is changing to a different group and will lose access
    const currentGroupId = currentProblem?.assignmentGroupId;
    const newGroupId = assignmentGroupId;
    const userRole = user?.role;
    const isGlobalAccessRole = userRole === 'super_admin' || userRole === 'admin' || userRole === 'manager';

    // Service Desk users can access all tickets, so skip the warning for them
    // If not a global access role, not a Service Desk user, and group is changing, check if user will lose access
    if (!isGlobalAccessRole && !isServiceDeskUser && currentGroupId !== newGroupId && onShowGroupChangeConfirmation) {
      // Check if user is member of new group
      const isUserInNewGroup = users?.some((member: any) => member.userId === user?.id);

      if (!isUserInNewGroup) {
        // Get the new group name for the confirmation modal
        const newGroup = groups.find((g: any) => g.id === newGroupId);

        // Show confirmation modal via callback
        onShowGroupChangeConfirmation({
          newGroupId: newGroupId,
          newGroupName: newGroup?.name || 'Unknown Group',
          pendingPayload: {
            problemState: 'assess_1',
            assignmentGroupId,
            assignedTo: assignedToId,
            ...(currentProblem?.problemDate && { problemDate: currentProblem.problemDate })
          }
        });

        // Close this modal
        onClose();
        return;
      }
    }

    setLoading(true);
    setError(null);

    try {
      // Update the problem with new assignment and state
      // Preserve problemDate from currentProblem if it exists
      const response = await api.put(`/cases/${problemId}`, {
        problemState: 'assess_1',
        assignmentGroupId,
        assignedTo: assignedToId,  // Backend expects 'assignedTo' field
        ...(currentProblem?.problemDate && { problemDate: currentProblem.problemDate })
      });

      onSuccess(response.data);
      onClose();
    } catch (error: any) {
      console.error('Error moving problem to assess:', error);
      setError(error.response?.data?.message || 'Failed to move problem to assess state');
    } finally {
      setLoading(false);
    }
  };

  const groups = Array.isArray(groupsData) ? groupsData : groupsData?.groups || [];
  const users = usersData || [];

  const filteredGroups = groupQuery === ''
    ? groups
    : groups.filter((group: any) =>
        group.name.toLowerCase().includes(groupQuery.toLowerCase())
      );

  const filteredUsers = userQuery === ''
    ? users
    : users.filter((user: any) =>
        `${user.firstName} ${user.lastName}`.toLowerCase().includes(userQuery.toLowerCase())
      );

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-10" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                <Dialog.Title
                  as="h3"
                  className="text-lg font-medium leading-6 text-gray-900 flex items-center justify-between"
                >
                  <span>Move to Assess 1</span>
                  <button
                    type="button"
                    className="text-gray-400 hover:text-gray-500"
                    onClick={onClose}
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </Dialog.Title>

                <div className="mt-2">
                  <p className="text-sm text-gray-500">
                    Assign this problem to a group and team member to begin the assessment process.
                  </p>
                </div>

                {error && (
                  <div className="mt-4 bg-red-50 border border-red-200 rounded-md p-4">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
                      </div>
                      <div className="ml-3">
                        <p className="text-sm text-red-800">{error}</p>
                      </div>
                    </div>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="mt-6 space-y-4">
                  {/* Assignment Group */}
                  <div>
                    <label htmlFor="assignmentGroup" className="block text-sm font-medium text-gray-700 mb-1">
                      Assignment Group <span className="text-red-500">*</span>
                    </label>
                    <Combobox
                      value={assignmentGroupId}
                      onChange={(value: string | null) => setAssignmentGroupId(value || '')}
                      disabled={loadingGroups}
                    >
                      <div className="relative">
                        <Combobox.Input
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-100"
                          displayValue={(groupId: string) => {
                            const group = groups.find((g: any) => g.id === groupId);
                            return group ? group.name : '';
                          }}
                          onChange={(event) => setGroupQuery(event.target.value)}
                          placeholder={loadingGroups ? 'Loading groups...' : 'Search assessment groups...'}
                        />
                        <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                          <ChevronUpDownIcon className="h-5 w-5 text-gray-400" />
                        </Combobox.Button>
                        <Transition
                          as={Fragment}
                          leave="transition ease-in duration-100"
                          leaveFrom="opacity-100"
                          leaveTo="opacity-0"
                          afterLeave={() => setGroupQuery('')}
                        >
                          <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                            {filteredGroups.length === 0 ? (
                              <div className="relative cursor-default select-none py-2 px-4 text-gray-700">
                                No groups found.
                              </div>
                            ) : (
                              filteredGroups.map((group: any) => (
                                <Combobox.Option
                                  key={group.id}
                                  value={group.id}
                                  className={({ active }) =>
                                    `relative cursor-pointer select-none py-2 pl-3 pr-9 ${
                                      active ? 'bg-indigo-600 text-white' : 'text-gray-900'
                                    }`
                                  }
                                >
                                  {({ active, selected }) => (
                                    <>
                                      <span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'}`}>
                                        {group.name}
                                      </span>
                                      {selected && (
                                        <span className={`absolute inset-y-0 right-0 flex items-center pr-4 ${active ? 'text-white' : 'text-indigo-600'}`}>
                                          <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                        </span>
                                      )}
                                    </>
                                  )}
                                </Combobox.Option>
                              ))
                            )}
                          </Combobox.Options>
                        </Transition>
                      </div>
                    </Combobox>
                  </div>

                  {/* Assigned To */}
                  <div>
                    <label htmlFor="assignedTo" className="block text-sm font-medium text-gray-700 mb-1">
                      Assigned To <span className="text-red-500">*</span>
                    </label>
                    <Combobox
                      value={assignedToId}
                      onChange={(value: string | null) => setAssignedToId(value || '')}
                      disabled={!assignmentGroupId || loadingUsers}
                    >
                      <div className="relative">
                        <Combobox.Input
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                          displayValue={(userId: string) => {
                            const user = users.find((u: any) => u.id === userId);
                            return user ? `${user.firstName} ${user.lastName}` : '';
                          }}
                          onChange={(event) => setUserQuery(event.target.value)}
                          placeholder={
                            !assignmentGroupId
                              ? 'Select Assignment Group first'
                              : loadingUsers
                              ? 'Loading users...'
                              : 'Search team members...'
                          }
                        />
                        <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                          <ChevronUpDownIcon className="h-5 w-5 text-gray-400" />
                        </Combobox.Button>
                        <Transition
                          as={Fragment}
                          leave="transition ease-in duration-100"
                          leaveFrom="opacity-100"
                          leaveTo="opacity-0"
                          afterLeave={() => setUserQuery('')}
                        >
                          <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                            {filteredUsers.length === 0 ? (
                              <div className="relative cursor-default select-none py-2 px-4 text-gray-700">
                                No users found.
                              </div>
                            ) : (
                              filteredUsers.map((user: any) => (
                                <Combobox.Option
                                  key={user.id}
                                  value={user.id}
                                  className={({ active }) =>
                                    `relative cursor-pointer select-none py-2 pl-3 pr-9 ${
                                      active ? 'bg-indigo-600 text-white' : 'text-gray-900'
                                    }`
                                  }
                                >
                                  {({ active, selected }) => (
                                    <>
                                      <span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'}`}>
                                        {user.firstName} {user.lastName} ({user.email})
                                      </span>
                                      {selected && (
                                        <span className={`absolute inset-y-0 right-0 flex items-center pr-4 ${active ? 'text-white' : 'text-indigo-600'}`}>
                                          <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                        </span>
                                      )}
                                    </>
                                  )}
                                </Combobox.Option>
                              ))
                            )}
                          </Combobox.Options>
                        </Transition>
                      </div>
                    </Combobox>
                    {!assignmentGroupId && (
                      <p className="mt-1 text-xs text-gray-500">
                        Please select an assignment group first
                      </p>
                    )}
                  </div>

                  <div className="mt-6 flex justify-end space-x-3">
                    <button
                      type="button"
                      className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                      onClick={onClose}
                      disabled={loading}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="inline-flex justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50"
                      disabled={loading || !assignmentGroupId || !assignedToId}
                    >
                      {loading ? 'Moving...' : 'Move to Assess 1'}
                    </button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default MoveToAssessModal;
